import React from 'react';
import { Button, Modal } from 'react-bootstrap';

import Alias from 'components/controls/alias';
import WidgetStatus from '../../../common/widgetStatus';

/**
 * DeleteCellDialog — confirm destroying a cell AND everything it owns, then show it happening.
 *
 * Deleting a cell used to be one unconfirmed click that removed a graph vertex. It now also removes
 * the cell's meta thread, every run's transcript and every artifact those runs published — work an
 * agent did, and the record of why. That is worth a sentence before it happens, and worth watching
 * while it does: a cell with fifty artifacts is fifty round trips, and a dialog that vanished on
 * the first one would leave the graph rearranging itself for several seconds with nothing said.
 *
 * The whole of "while it does" is one line — how far along, and which object is going right now.
 * A per-object log was tried and dropped: fifty rows scrolling past is not something anybody reads,
 * and the objects are the ones the sentence above already named.
 *
 * NOT `controls/confirmModal`, which is the app's shared confirmation and the right thing for
 * almost everything: it dispatches and closes in the same breath, so there is nowhere to put the
 * progress. Everything else about it is copied deliberately — size, static backdrop, a light
 * dismiss beside a success confirm — so this reads as one of the family rather than as a stranger.
 *
 * A cell that owns nothing never gets here; the surface deletes it outright, because the only thing
 * lost is an empty cell and a dialog to say so is furniture. So this dialog always has something
 * concrete to name.
 *
 * Props:
 *   show      — boolean
 *   title     — the cell's title, for the sentence
 *   summary   — "its meta thread, 2 run transcripts and 5 artifacts" (domain/cellObjects.js)
 *   progress  — null before it starts, then { done, total, alias } for the object in flight
 *   error     — string; what went wrong, when something did
 *   onConfirm — () => void
 *   onHide    — () => void
 */
export default function DeleteCellDialog({ show, title, summary, progress, error, onConfirm, onHide }) {
  const busy = !!progress && !error;
  const confirmRef = React.useRef(null);

  // Return answers the question, because that is what Return does to a question — and this dialog
  // asks one, with no field to type in that could want the key for itself.
  //
  // By FOCUSING the confirm as the dialog opens, rather than by handling the key. Three reasons,
  // the last of which is decisive:
  //
  //  - a focus ring shows which answer Return will give, where a hidden handler would not;
  //  - Space, and every other way a button is pressed, comes along for free;
  //  - the key cannot be handled here anyway. `onKeyDown` on a react-bootstrap `<Modal>` lands on
  //    `.modal-dialog` (Modal.js spreads the rest of its props onto the inner Dialog), while the
  //    thing that HOLDS focus is the `.modal` container wrapping it. Events travel up, so a
  //    keydown on the container never reaches a listener on its own child — it silently does
  //    nothing, which is what it did.
  //
  // `onEntered`, not `autoFocus`: the modal takes focus for itself when it opens, after its
  // children have mounted, so a child's own autoFocus is given and then taken away.
  //
  // What this cannot do is delete behind a person's back. Focus is moved once, onto the safest
  // thing that can be pressed; a person who tabs to No gets No, and while the deletes run the
  // confirm is disabled and Return does nothing at all.
  const focusConfirm = () => confirmRef.current && confirmRef.current.focus();

  return (
    // Not dismissible while the work is running: a backdrop click that closed the dialog would not
    // stop the deletes, and would leave a person watching a graph change with no idea why.
    <Modal show={show} onHide={busy ? undefined : onHide} size="lg" backdrop="static"
      onEntered={focusConfirm}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Delete Cell</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className={progress || error ? 'mb-3' : 'mb-0'}>
          Delete <strong>{title}</strong> and {summary}?
        </p>
        {progress && !error && (
          <WidgetStatus variant="primary" loading className="mb-0">
            {/* The alias ellipsizes rather than wrapping (Alias's chip variant), so an artifact
                path several folders deep keeps this to one line instead of reflowing the dialog
                every time the object in flight changes. */}
            <span className="mc-delete-progress">
              <span className="mc-delete-count">Deleting {progress.done}/{progress.total}</span>
              {progress.alias && <Alias value={progress.alias} variant="chip" />}
            </span>
          </WidgetStatus>
        )}
        {error && <WidgetStatus variant="warning" className="mb-0">{error}</WidgetStatus>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" disabled={busy} onClick={onHide}>
          {error ? 'Close' : 'No'}
        </Button>
        {!error && (
          <Button ref={confirmRef} variant="success" disabled={busy} onClick={onConfirm}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
